import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { HiArrowLeft, HiMinus, HiPlus } from 'react-icons/hi'
import { useShopProduct } from '../hooks/useShop'
import { useCart } from '../context/CartContext'
import Button from '../components/ui/Button'
import CTA from '../components/home/CTA'

export default function ProductDetail() {
  const { id } = useParams()
  const { data: product, isLoading, isError } = useShopProduct(id)
  const { addItem, pendingId } = useCart()

  const [quantity, setQuantity] = useState(1)
  const [activeImage, setActiveImage] = useState(0)

  const isAdding = pendingId === Number(id)

  if (isLoading) {
    return (
      <div className="pt-28 pb-16 container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square skeleton rounded-3xl" />
          <div className="space-y-4">
            <div className="h-8 w-2/3 skeleton rounded-xl" />
            <div className="h-4 w-1/3 skeleton rounded-xl" />
            <div className="h-32 skeleton rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  if (isError || !product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-24 px-4">
        <div className="text-center">
          <p className="text-5xl mb-4">🔍</p>
          <h2 className="font-display text-2xl text-ink mb-4">Product not found</h2>
          <Button to="/shop" variant="primary">Back to Shop</Button>
        </div>
      </div>
    )
  }

  const images = [product.image, ...(product.gallery ?? [])].filter((img) => img?.url)
  const current = images[activeImage] ?? images[0]

  return (
    <div className="pt-28 pb-16 bg-gradient-to-b from-background to-light-100">
      <div className="container-custom">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 font-body text-sm text-ink-muted hover:text-primary transition-colors mb-8"
        >
          <HiArrowLeft size={16} /> Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* ── Gallery ────────────────────────────────────────────────── */}
          <div>
            <motion.div
              key={current?.url}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="aspect-square rounded-3xl overflow-hidden bg-light-200 shadow-card"
            >
              <img
                src={current?.url || '/placeholder.png'}
                alt={current?.alt || product.name}
                className="w-full h-full object-cover"
              />
            </motion.div>

            {images.length > 1 && (
              <div className="flex gap-3 mt-4 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={img.url + i}
                    onClick={() => setActiveImage(i)}
                    className={[
                      'w-20 h-20 shrink-0 rounded-xl overflow-hidden border-2 transition-colors',
                      activeImage === i ? 'border-primary' : 'border-transparent opacity-70 hover:opacity-100',
                    ].join(' ')}
                  >
                    <img src={img.url} alt={img.alt || product.name} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Details ────────────────────────────────────────────────── */}
          <div>
            {/* Categories */}
            {product.categories?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {product.categories.map((cat) => (
                  <span
                    key={cat.id}
                    className="px-3 py-0.5 rounded-full text-xs font-body bg-primary/10 text-primary"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            )}

            <h1 className="font-display text-3xl md:text-4xl text-ink mb-3 leading-tight">
              {product.name}
            </h1>

            {/* Price */}
            <div className="flex items-end gap-3 mb-4">
              {product.is_on_sale && product.regular_price && (
                <p className="font-body text-lg text-ink-muted line-through">
                  {product.currency_symbol}{Number(product.regular_price).toLocaleString()}
                </p>
              )}
              <p className="font-display text-3xl text-primary">
                {product.currency_symbol}{Number(product.price).toLocaleString()}
                <span className="text-base text-ink-muted font-body ml-1">{product.currency}</span>
              </p>
              {product.is_on_sale && (
                <span className="px-3 py-1 rounded-full text-xs font-body bg-accent/10 text-accent font-semibold">
                  Sale
                </span>
              )}
            </div>

            {/* Stock status */}
            <p className="font-body text-sm mb-6">
              {product.in_stock
                ? <span className="text-green-600 font-semibold">✓ In Stock</span>
                : <span className="text-red-500 font-semibold">Out of Stock</span>}
            </p>

            {/* Description */}
            {product.description && (
              <div
                className="prose prose-sm max-w-none font-body text-ink-muted mb-8 [&_*]:font-body"
                dangerouslySetInnerHTML={{ __html: product.description }}
              />
            )}

            {/* Quantity + Add to cart */}
            {product.purchasable && product.in_stock ? (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3 px-4 py-3 rounded-full border border-light-400">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-6 h-6 rounded-full bg-light-200 flex items-center justify-center hover:bg-light-300 transition-colors"
                  >
                    <HiMinus size={12} />
                  </button>
                  <span className="font-body text-sm w-6 text-center tabular-nums">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => q + 1)}
                    className="w-6 h-6 rounded-full bg-light-200 flex items-center justify-center hover:bg-light-300 transition-colors"
                  >
                    <HiPlus size={12} />
                  </button>
                </div>

                <Button
                  variant="primary"
                  className="flex-1"
                  loading={isAdding}
                  disabled={isAdding}
                  onClick={() => addItem(product.id, quantity)}
                >
                  {isAdding ? 'Adding…' : 'Add to Cart'}
                </Button>
              </div>
            ) : (
              <Button variant="primary" disabled className="w-full opacity-50 cursor-not-allowed">
                Out of Stock
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-24">
        <CTA />
      </div>
    </div>
  )
}
